import React from "react";

/**
 * SIMO-HUMAN-COMMUNICATION-UX-1.
 *
 * Bedienoberflaeche fuer den Human Status Contract
 * (`apps/server/src/human-status-contract.ts`). Diese Komponente ist bewusst
 * ein reiner Renderer: sie kennt den Contract nicht als TypeScript-Typ
 * (Server-/UI-Grenze bleibt entkoppelt), sondern nimmt das bereits vom
 * Server projizierte Objekt entgegen und zeigt exakt das an, was darin
 * steht — nicht mehr und nicht weniger.
 *
 * Owner-Vorgabe: Ergebnis und notwendige Owner-Handlung zuerst, technische
 * Nachweise separat und standardmaessig eingeklappt. Harte Invariante: die
 * einfache Ansicht darf Information nur AUSBLENDEN, niemals entfernen,
 * kuerzen oder umdeuten.
 */

export type HumanStatusLevel = "simple" | "standard" | "technical";

export interface HumanStatusCardProps {
  /** Projizierter Contract vom Server (`projectHumanStatus`-Ergebnis) oder `null`. */
  status: Record<string, unknown> | null;
  level: HumanStatusLevel;
  onLevelChange: (level: HumanStatusLevel) => void;
  error?: Error | null;
}

const STATE_LABELS: Record<string, string> = {
  running: "Läuft",
  succeeded: "Erfolgreich",
  limited: "Eingeschränkt",
  failed: "Fehlgeschlagen",
  blocked: "Blockiert",
};

const SEVERITY_LABELS: Record<string, string> = {
  info: "Hinweis",
  limitation: "Einschränkung",
  risk: "Risiko",
  blocker: "Blocker",
};

const LEVELS: Array<{ level: HumanStatusLevel; label: string }> = [
  { level: "simple", label: "Einfach" },
  { level: "standard", label: "Standard" },
  { level: "technical", label: "Technisch" },
];

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

interface OpenItemLike {
  text: string;
  severity: string;
}

function isOpenItemArray(value: unknown): value is OpenItemLike[] {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as Record<string, unknown>).text === "string" &&
        typeof (entry as Record<string, unknown>).severity === "string",
    )
  );
}

interface OwnerActionLike {
  required: boolean;
  text: string | null;
}

function isOwnerActionLike(value: unknown): value is OwnerActionLike {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Record<string, unknown>).required === "boolean"
  );
}

interface ProgressLike {
  step: number;
  total: number;
  label: string;
}

function isProgressLike(value: unknown): value is ProgressLike {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Record<string, unknown>).step === "number" &&
    typeof (value as Record<string, unknown>).total === "number" &&
    typeof (value as Record<string, unknown>).label === "string"
  );
}

/**
 * Verstaendliche Fehlerzeile ohne technische Rohmeldung. Wie bei
 * `WorkerRunnerPanel`/`ownerFacingGlobalError`: die rohe `error.message`
 * (kann Provider-/Connector-Details, Stacktraces oder interne IDs enthalten)
 * wird NIE direkt gerendert, nur ein sprechender Owner-Satz.
 */
function humanStatusErrorCopy(_error: Error): string {
  return "Die Statusmeldung konnte nicht geladen werden. Bitte Ansicht neu laden.";
}

function LevelSwitcher({
  level,
  onLevelChange,
}: {
  level: HumanStatusLevel;
  onLevelChange: (level: HumanStatusLevel) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Detailgrad">
      {LEVELS.map((entry) => (
        <button
          key={entry.level}
          type="button"
          className="btn px-2 py-1 text-[11px]"
          aria-pressed={entry.level === level}
          onClick={() => onLevelChange(entry.level)}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}

export function HumanStatusCard(props: HumanStatusCardProps): JSX.Element {
  const { status, level, onLevelChange, error } = props;

  if (error) {
    return (
      <div className="space-y-2">
        <LevelSwitcher level={level} onLevelChange={onLevelChange} />
        <div className="rounded-md border border-jarvis-border/45 bg-jarvis-bg/25 px-3 py-3">
          {/* Die Roh-`error.message` wird bewusst NICHT gerendert — nur ein
              verstaendlicher Owner-Satz, analog zu WorkerRunnerPanel. */}
          <p className="text-[12px] text-rose-300">{humanStatusErrorCopy(error)}</p>
        </div>
      </div>
    );
  }

  if (status === null) {
    return (
      <div className="space-y-2">
        <LevelSwitcher level={level} onLevelChange={onLevelChange} />
        <div className="rounded-md border border-jarvis-border/45 bg-jarvis-bg/25 px-3 py-3">
          <p className="text-[12px] text-jarvis-muted">Noch keine Statusmeldung.</p>
        </div>
      </div>
    );
  }

  const state = status.state;
  const stateLabel = typeof state === "string" ? STATE_LABELS[state] ?? state : null;
  const progress = isProgressLike(status.progress) ? status.progress : null;

  const headline = isNonEmptyString(status.headline) ? status.headline : null;
  const meaning = isNonEmptyString(status.meaning) ? status.meaning : null;
  const changed = isStringArray(status.changed) ? status.changed : null;
  const why = isNonEmptyString(status.why) ? status.why : null;
  const working = isStringArray(status.working) ? status.working : null;
  const notWorking = isOpenItemArray(status.notWorking) ? status.notWorking : null;
  const ownerAction = isOwnerActionLike(status.ownerAction) ? status.ownerAction : null;
  const nextStep = status.nextStep !== undefined && status.nextStep !== null && isNonEmptyString(status.nextStep) ? status.nextStep : null;
  const technical =
    status.technical !== undefined && typeof status.technical === "object" && status.technical !== null
      ? (status.technical as Record<string, string | number | boolean | null>)
      : null;

  return (
    <div className="space-y-3">
      <LevelSwitcher level={level} onLevelChange={onLevelChange} />

      <div className="space-y-3 rounded-md border border-jarvis-border/45 bg-jarvis-bg/25 px-3 py-3">
        {/* 1. Statuszeile */}
        {stateLabel !== null && (
          <div className="text-[12px] font-medium text-jarvis-text">
            {stateLabel}
            {progress !== null && ` — Schritt ${progress.step} von ${progress.total} — ${progress.label}`}
          </div>
        )}

        {/* 2. Ergebnis */}
        {headline !== null && (
          <div>
            <h3 className="text-base font-semibold text-jarvis-text">{headline}</h3>
          </div>
        )}

        {/* 3. Was bedeutet das? */}
        {meaning !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Was bedeutet das?</h4>
            {/* Wert wird unveraendert gerendert — kein Kuerzen, kein Umformulieren. */}
            <p className="mt-1 text-[13px] text-jarvis-text">{meaning}</p>
          </div>
        )}

        {/* 4. Was wurde geändert? */}
        {changed !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Was wurde geändert?</h4>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] text-jarvis-text">
              {changed.map((entry, index) => (
                // Wert wird unveraendert gerendert — kein Kuerzen, kein Umformulieren.
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 5. Warum? */}
        {why !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Warum?</h4>
            <p className="mt-1 text-[13px] text-jarvis-text">{why}</p>
          </div>
        )}

        {/* 6. Was funktioniert jetzt? */}
        {working !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Was funktioniert jetzt?</h4>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] text-jarvis-text">
              {working.map((entry, index) => (
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 7. Was funktioniert noch nicht? */}
        {notWorking !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Was funktioniert noch nicht?</h4>
            <ul className="mt-1 space-y-1 pl-0 text-[13px] text-jarvis-text">
              {notWorking.map((entry, index) => (
                <li key={index} className="flex flex-wrap items-baseline gap-1.5">
                  <span className="badge bg-jarvis-bg/60 text-[10px] text-jarvis-muted">
                    {SEVERITY_LABELS[entry.severity] ?? entry.severity}
                  </span>
                  <span>{entry.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 8. Musst du etwas tun? */}
        {ownerAction !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Musst du etwas tun?</h4>
            {ownerAction.required === false ? (
              <p className="mt-1 text-[13px] text-jarvis-text">Nein.</p>
            ) : (
              <p className="mt-1 rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-1.5 text-[13px] font-medium text-amber-100">
                {ownerAction.text}
              </p>
            )}
          </div>
        )}

        {/* 9. Nächster sinnvoller Schritt — nur wenn nicht null */}
        {nextStep !== null && (
          <div>
            <h4 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Nächster sinnvoller Schritt</h4>
            <p className="mt-1 text-[13px] text-jarvis-text">{nextStep}</p>
          </div>
        )}

        {/* 10. Technische Nachweise — nur wenn im projizierten Objekt vorhanden,
            und dann in einem standardmaessig geschlossenen <details> (kein
            `open`-Attribut). */}
        {technical !== null && (
          <details className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/20 px-2 py-1.5">
            <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">
              Technische Nachweise
            </summary>
            <dl className="mt-2 grid grid-cols-1 gap-1 text-[11px] text-jarvis-text">
              {Object.entries(technical).map(([key, value]) => (
                <div key={key} className="flex flex-wrap items-baseline gap-1.5">
                  <dt className="shrink-0 font-mono text-jarvis-muted">{key}</dt>
                  <dd className="min-w-0 break-words font-mono">{String(value)}</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </div>
    </div>
  );
}
